'use client'

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { StorageKeys } from '../constants/storagekeys'
import { getCompanyDetailsByIds, getUserMultiCompanies } from '@/app/service/companyService'
import { CompanyDetailsApiResponse } from '@/app/service/models/companyDetails'
import { AffiliationCdEnum, BrandCdEnum } from '@/app/service/models/expApiTypes'
import { ExpApiError, extractApiErrorDetails, formatCompanyErrorSupport } from '@/app/lib/errors/apiError'
import { ExpApiErrorObject } from '@/app/lib/errors/models/errorTypes'
import { buildBrandDropdownList, buildCompanyDropdownList, DropdownOption } from '@/app/utilities/companyListHelpers'
import { findCompanyById } from '@/app/utilities/companyHelpers'
import { UserPermissionsResponse } from '../constants/permissions'
import { isCompassPocUser, getSubFromIdToken } from '../constants/compassPoc'
import { hasAcceptedTerms } from '../../app/service/userPreferencesService'
import { useUserContext } from './userContext'
import { SessionData } from '@/app/adapters/types'
import ErrorCardView, { ErrorCardVariant } from '@/app/components/ui/errorComponents/errorCardView'
import { AppRoutes } from '../constants/routes'

interface CompanyContextType {
	isLoading: boolean
	companyContextError: Error | ExpApiErrorObject | ExpApiError | undefined
	selectedCompanyId: string | null
	selectedCompany: CompanyDetailsApiResponse | null
	availableCompaniesById: CompanyDetailsApiResponse[]
	availableCompanies: CompanyDetailsApiResponse[]
	availableBrandCodes: BrandCdEnum[]
	isMultiCompany: boolean
	brandList: DropdownOption[]
	companyList: DropdownOption[]
	selectedBrand: string
	selectedCompanyForUI: DropdownOption | null
	hasAcceptedLatestTerms: boolean | null
	permissions: string[] | null
	brandCode: string | null
	affiliationTypeCd: string
	isCbAaUser: boolean
	onChangeBrand: (brandCode: string) => void
	onChangeCompany: (company: DropdownOption | null, autoSelect?: boolean) => void
	selectCompany: (companyId: string) => void
	refresh: () => Promise<void>
	refreshHasAcceptedTerms: (companyIdOverride?: string) => Promise<boolean>
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined)

/** Extract uppercase BROKER party IDs from a permissions response. */
function extractBrokerCompanyIds(multiCompanyResponse: UserPermissionsResponse | unknown): string[] {
	if (
		!multiCompanyResponse ||
		!('partyPermissions' in (multiCompanyResponse as UserPermissionsResponse)) ||
		!Array.isArray((multiCompanyResponse as UserPermissionsResponse).partyPermissions)
	) {
		return []
	}

	const seen = new Map<string, boolean>()
	for (const party of (multiCompanyResponse as UserPermissionsResponse).partyPermissions) {
		const partyType = (party.partyTypeCd ?? '').toString().toUpperCase()
		const partyId = (party.partyId ?? '').toString()
		if (partyType === 'BROKER' && partyId) {
			seen.set(partyId.toUpperCase(), true)
		}
	}
	return Array.from(seen.keys())
}

/** Sort and store the permissions response, returning the sorted version. */
function sortPermissionsResponse(response: UserPermissionsResponse): UserPermissionsResponse {
	return {
		...response,
		partyPermissions: (response.partyPermissions ?? []).sort((a, b) =>
			(a.partyId ?? '').toString().localeCompare((b.partyId ?? '').toString())
		),
	}
}

export const CompanyProvider: React.FC<{ sessionData: SessionData; children: ReactNode }> = ({
	sessionData,
	children,
}) => {
	const { isLoading: isUserLoading } = useUserContext()
	const { tempBwc28EnableDashSecurity } = useFlags()

	const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
	const [availableCompaniesById, setAvailableCompaniesById] = useState<CompanyDetailsApiResponse[]>([])
	const [availableCompanies, setAvailableCompanies] = useState<CompanyDetailsApiResponse[]>([])
	const [availableBrandCodes, setAvailableBrandCodes] = useState<BrandCdEnum[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [companyContextError, setCompanyContextError] = useState<Error | ExpApiErrorObject | ExpApiError | undefined>()

	const [selectedBrand, setSelectedBrand] = useState<string>('selectone')
	const [selectedCompanyForUI, setSelectedCompanyForUI] = useState<DropdownOption | null>(null)
	const [isMultiCompany, setIsMultiCompany] = useState<boolean>(false)
	const [permissionsResponse, setPermissionsResponse] = useState<UserPermissionsResponse>()
	const [hasAcceptedLatestTerms, setHasAcceptedLatestTerms] = useState<boolean | null>(null)
	const [selectedCompany, setSelectedCompany] = useState<CompanyDetailsApiResponse | null>(null)
	const [permissions, setPermissions] = useState<string[] | null>(null)
	const [brandCode, setBrandCode] = useState<string | null>(null)
	const [affiliationTypeCd, setAffiliationTypeCd] = useState<string>('UNK')
	const [isCbAaUser, setIsCbAaUser] = useState<boolean>(false)
	useEffect(() => {
		if (status === 'unauthenticated') {
			setIsLoading(false)
		}
	}, [status])
	// ── Derive and set all values that depend on the selected company ────
	const applyCompanySelection = useCallback(
		(companyId: string | null, companies: CompanyDetailsApiResponse[], permsResponse?: UserPermissionsResponse) => {
			const company = companyId ? findCompanyById(companies, companyId) : null
			setSelectedCompany(company)

			const perms = (() => {
				if (!companyId || !permsResponse?.partyPermissions) return null
				const target = companyId.toUpperCase().trim()
				const party = permsResponse.partyPermissions.find(p => (p?.partyId ?? '').toUpperCase().trim() === target)
				return party?.accessPermissions ?? []
			})()
			setPermissions(perms)

			const bc = company?.brandCd ?? null
			const affiliation = company?.affiliationTypeCd ?? 'UNK'
			setBrandCode(bc)
			setAffiliationTypeCd(affiliation)
			setIsCbAaUser(bc === BrandCdEnum.CBR && affiliation === AffiliationCdEnum.OWN)
		},
		[]
	)

	// ── Internal: fetch company data from APIs ──────────────────────────
	const refreshCompanies = useCallback(async (): Promise<string | null> => {
		// Resolve user ID from session — try session.user.id first, then parse sub from idToken
		const userId =
			sessionData?.user?.id ??
			sessionData?.oktaId ??
			(() => {
				try {
					const idToken = sessionData?.idToken
					if (!idToken) return null
					const payload = JSON.parse(Buffer.from(idToken.split('.')[1], 'base64').toString())
					return payload?.sub ?? null
				} catch {
					return null
				}
			})()

		if (!userId || !sessionData?.accessToken) {
			console.warn('CompanyContext – refreshCompanies bailing: no userId or accessToken')
			return null
		}

		// Compass POC test users are not synced in Permit.io, so expapi
		// rejects their company details call. Short-circuit here and let
		// the route guard redirect them to the POC dashboard.
		if (isCompassPocUser(sessionData?.user?.email ?? getSubFromIdToken(sessionData?.idToken))) {
			setAvailableCompaniesById([])
			setAvailableCompanies([])
			setAvailableBrandCodes([])
			setIsMultiCompany(false)
			setPermissions([])
			setSelectedCompany(null)
			setSelectedCompanyId(null)
			return null
		}

		setCompanyContextError(undefined)

		try {
			let multiCompanyResponseUnknown: unknown
			try {
				multiCompanyResponseUnknown = await getUserMultiCompanies(
					userId,
					sessionData?.accessToken ?? '',
					Boolean(tempBwc28EnableDashSecurity)
				)
			} catch (permissionsErr) {
				const errorDetails = extractApiErrorDetails(permissionsErr, 'Failed to fetch user permissions')
				setCompanyContextError(errorDetails)
				setSelectedCompanyId(null)
				setHasAcceptedLatestTerms(null)
				setAvailableCompaniesById([])
				setAvailableBrandCodes([])
				setIsMultiCompany(false)
				applyCompanySelection(null, [])
				return null
			}

			const isValidResponse = Boolean(multiCompanyResponseUnknown && !Array.isArray(multiCompanyResponseUnknown))
			const permissionsResponseData = isValidResponse
				? (multiCompanyResponseUnknown as UserPermissionsResponse)
				: undefined
			if (permissionsResponseData) {
				setPermissionsResponse(sortPermissionsResponse(permissionsResponseData))
			} else {
				setPermissionsResponse(undefined)
			}

			let companyIds = extractBrokerCompanyIds(multiCompanyResponseUnknown)

			// If the permissions response is valid but partyPermissions is
			// explicitly empty, the user has no company access — do NOT fall
			// back to the session companyId.
			const hasValidEmptyPermissions =
				isValidResponse &&
				Array.isArray(permissionsResponseData?.partyPermissions) &&
				permissionsResponseData.partyPermissions.length === 0

			if (!companyIds?.length) {
				if (hasValidEmptyPermissions) {
					// User genuinely has zero permissions — treat as no access.
					// Set permissions to empty array (not null) so the route
					// guard can distinguish "no permissions" from "still loading".
					console.error('[UNAUTHORIZED] User has no companies available', {
						selectedCompanyId: null,
						availableCompaniesCount: 0,
						permissionsLoaded: true,
						permissionsCount: 0,
					})
					setAvailableCompanies([])
					setAvailableCompaniesById([])
					setAvailableBrandCodes([])
					setIsMultiCompany(false)
					setPermissions([])
					setSelectedCompany(null)
					return null
				}

				// Fallback: if no BROKER companies found in permissions, use the
				// company ID from the user's session (Okta profile claim).
				// This handles single-company users who don't have a BROKER party type.
			const sessionCompanyId = sessionData?.user?.companyId?.toUpperCase()
				if (sessionCompanyId) {
					companyIds = [sessionCompanyId]
				} else {
					setAvailableCompanies([])
					setAvailableCompaniesById([])
					setAvailableBrandCodes([])
					setIsMultiCompany(false)
					applyCompanySelection(null, [])
					return null
				}
			}

			const responseByIds = await getCompanyDetailsByIds(companyIds, sessionData?.accessToken)

			// Ensure all company IDs are uppercase
			const companiesWithUppercaseIds = responseByIds.companies.map(company => ({
				...company,
				companyId: company.companyId.toUpperCase(),
			}))

			// Sort by companyId (GUID string) lexicographically for consistency
			const sortedCompanies = companiesWithUppercaseIds.toSorted((a, b) => a.companyId.localeCompare(b.companyId))
			// Sort brand codes alphabetically for consistent display
			const sortedBrandCodes = responseByIds.brandCodes.toSorted((a, b) => a.localeCompare(b))

			setAvailableCompanies(sortedCompanies)
			setAvailableCompaniesById(sortedCompanies)
			setAvailableBrandCodes(sortedBrandCodes)
			setIsMultiCompany(responseByIds.companies.length > 1)

			// Resolve which company to select
			const sortedPermsResponse = permissionsResponseData
				? sortPermissionsResponse(permissionsResponseData)
				: undefined

			// Restore from localStorage or auto-select single company
			let resolvedCompanyId = selectedCompanyId
			if (!resolvedCompanyId) {
				const storedId =
					typeof window !== 'undefined'
						? (localStorage.getItem(StorageKeys.COOKIE_COMPANY_ID)?.toUpperCase() ?? null)
						: null
				const validStoredId = storedId ? sortedCompanies.find(c => c.companyId === storedId) : null

				if (validStoredId) {
					resolvedCompanyId = storedId
				} else if (responseByIds.companies.length === 1) {
					resolvedCompanyId = companyIds[0].toUpperCase()
					localStorage.setItem(StorageKeys.COOKIE_COMPANY_ID, resolvedCompanyId)
				}
			}

			// Set company ID and derive all dependent values in one batch
			setSelectedCompanyId(resolvedCompanyId)
			applyCompanySelection(resolvedCompanyId, sortedCompanies, sortedPermsResponse)
			return resolvedCompanyId
		} catch (err) {
			const errorDetails = extractApiErrorDetails(err, 'Failed to refresh companies')
			setCompanyContextError(errorDetails)
			setSelectedCompanyId(null)
			setHasAcceptedLatestTerms(null)
			setAvailableCompaniesById([])
			setAvailableBrandCodes([])
			setIsMultiCompany(false)
			applyCompanySelection(null, [])
			return null
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		sessionData?.accessToken,
		sessionData?.user?.id,
		sessionData?.user?.email,
		selectedCompanyId,
		tempBwc28EnableDashSecurity,
	])

	// ── Check terms acceptance for the selected company ───────────────────
	// Optional companyIdOverride allows callers to pass the just-resolved ID
	// when the state hasn't re-rendered yet (e.g. inside refresh after auto-select).
	const refreshHasAcceptedTerms = useCallback(
		async (companyIdOverride?: string): Promise<boolean> => {
			const companyId = companyIdOverride ?? selectedCompanyId
			if (!companyId) {
				setHasAcceptedLatestTerms(false)
				return false
			}
			try {
				const response = await hasAcceptedTerms(companyId, sessionData?.accessToken)
				setHasAcceptedLatestTerms(!!response)
				return !!response
			} catch (error) {
				console.error('Error in refreshHasAcceptedTerms:', error)
				setHasAcceptedLatestTerms(false)
				return false
			}
		},
		[sessionData?.accessToken, selectedCompanyId]
	)

	// ── Public refresh(): orchestrates full data load cycle ──────────────
	// Calls refreshCompanies + refreshHasAcceptedTerms, manages isLoading.
	// try/finally guarantees isLoading is always reset — no stuck states.
	const refresh = useCallback(async () => {
		setIsLoading(true)

		try {
			const resolvedCompanyId = await refreshCompanies()
			// Pass the just-resolved ID since selectedCompanyId state hasn't
			// re-rendered yet — the closure still sees the old value.
			if (resolvedCompanyId) {
				await refreshHasAcceptedTerms(resolvedCompanyId)
			}
		} catch (error) {
			console.error('CompanyContext - Failed to refresh:', error)
		} finally {
			setIsLoading(false)
		}
	}, [refreshCompanies, refreshHasAcceptedTerms])

	// ── Effects ──────────────────────────────────────────────────────────

	// Effect: auto-refresh when session token becomes available AND user context is ready.
	// Gating on isUserLoading prevents CompanyContext from fetching with incomplete
	// session data and then re-fetching once UserContext finishes.
	useEffect(() => {
		if (isUserLoading) {
			return
		}
		if (sessionData?.accessToken) {
			refresh()
		} else {
			setIsLoading(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionData?.accessToken, isUserLoading])

	const selectCompany = useCallback(
		async (selectedId: string) => {
			setCompanyContextError(undefined)
			const company = findCompanyById(availableCompaniesById, selectedId)
			if (company) {
				const upperId = selectedId.toUpperCase()
				setIsLoading(true)
				// Reset terms status immediately so the route guard does not
				// proceed with a stale value from the previous company
				setHasAcceptedLatestTerms(null)
				setSelectedCompanyId(upperId)
				applyCompanySelection(upperId, availableCompaniesById, permissionsResponse)

				localStorage.setItem(StorageKeys.COOKIE_COMPANY_ID, upperId)
				if (company.companyNm) {
					localStorage.setItem(StorageKeys.COOKIE_COMPANY_NAME, company.companyNm)
				}

				// Re-check terms for the newly selected company
				try {
					await hasAcceptedTerms(upperId, sessionData?.accessToken).then(response => {
						setHasAcceptedLatestTerms(!!response)
					})
				} catch (error) {
					console.error('Error checking terms for selected company:', error)
					setHasAcceptedLatestTerms(false)
				} finally {
					setIsLoading(false)
				}
			}
		},
		[availableCompaniesById, permissionsResponse, applyCompanySelection, sessionData?.accessToken]
	)

	const onChangeBrand = useCallback((bc: string) => {
		setSelectedBrand(bc)
		setSelectedCompanyForUI(null)
	}, [])

	const onChangeCompany = useCallback(
		(company: DropdownOption | null, autoSelect: boolean = true) => {
			setSelectedCompanyForUI(company)
			// Only proceed to select the company in context if autoSelect is true
			if (
				autoSelect &&
				company?.value &&
				availableCompanies.some((c: { companyId: string }) => c.companyId === company.value)
			) {
				selectCompany(company.value)
			}
		},
		[selectCompany, availableCompanies]
	)

	const brandList = useMemo(() => {
		const list = buildBrandDropdownList(availableCompaniesById)
		return list
	}, [availableCompaniesById])

	const companyList = useMemo(() => {
		const mappedCompanyList = buildCompanyDropdownList(availableCompaniesById, selectedBrand)
		return mappedCompanyList
	}, [availableCompaniesById, selectedBrand])

	const supportLines = useMemo(() => formatCompanyErrorSupport(companyContextError), [companyContextError])

	const contextValue = useMemo(
		() => ({
			selectedCompanyId,
			selectedCompany,
			availableCompaniesById,
			availableCompanies,
			availableBrandCodes,
			isMultiCompany,
			brandList,
			companyList,
			selectedBrand,
			onChangeBrand,
			selectedCompanyForUI,
			hasAcceptedLatestTerms,
			permissions,
			brandCode,
			affiliationTypeCd,
			isCbAaUser,
			onChangeCompany,
			selectCompany,
			refresh,
			refreshHasAcceptedTerms,
			isLoading,
			companyContextError,
		}),
		[
			selectedCompanyId,
			selectedCompany,
			availableCompaniesById,
			availableCompanies,
			availableBrandCodes,
			isMultiCompany,
			brandList,
			companyList,
			selectedBrand,
			hasAcceptedLatestTerms,
			permissions,
			brandCode,
			affiliationTypeCd,
			isCbAaUser,
			onChangeBrand,
			selectedCompanyForUI,
			onChangeCompany,
			selectCompany,
			refresh,
			refreshHasAcceptedTerms,
			isLoading,
			companyContextError,
		]
	)

	const is403 = (companyContextError as ExpApiErrorObject)?.httpStatusCd === 403
	if (is403) {
		return (
			<ErrorCardView
				variant={ErrorCardVariant.UNAUTHORIZED}
				onPrimaryAction={() => {
					window.location.href = AppRoutes.AUTH_SIGNOUT
				}}
			/>
		)
	}
	if (companyContextError) {
		return (
			<ErrorCardView
				variant={ErrorCardVariant.NETWORK_ERROR}
				onPrimaryAction={() => window.location.reload()}
				supportLines={supportLines}
				supportTextTestId="network-error-support-text"
			/>
		)
	}

	return <CompanyContext.Provider value={contextValue}>{children}</CompanyContext.Provider>
}

export const useCompanyContext = (): CompanyContextType => {
	const context = useContext(CompanyContext)
	if (context === undefined) {
		throw new Error('useCompanyContext must be used within a CompanyProvider')
	}
	return context
}
